import { For, Show, createMemo, type Component } from 'solid-js';
import { Panel } from '~ui/panel';
import { Toolbar, ToolbarSection } from '~ui/toolbar';
import { Heading, Text } from '~ui/text';
import { Button } from '~ui/button';
import { CrossIcon } from '~ui/icons';
import { formatPercent } from '~/lib/format';
import { allocationsByKind } from '~data';
import { lookThrough, isNested, type Leaf } from '~/data/look-through';
import { matchSector } from '~/data/sector-canon';
import { closeSector, drillInto, openSector, selectedSymbol } from '~/stores/explore-store';

// What is actually inside one slice of a fund's sector breakdown.
//
// The issuer publishes the slice and the holdings but never the join between
// them, so which holding sits in which sector is the app's own classification —
// see src/data/sectors.ts, and `bun run check:sectors`.
//
// Two things make this harder than filtering a list:
//
// The holdings may not be companies. HYLD's are nine Hamilton ETFs, and the
// technology it reports is a level or two further down, inside QMAX and inside
// the index fund QMAX holds. So the list comes from a full look-through rather
// than from the fund's own holdings — see src/data/look-through.ts.
//
// And the labels may not match. HYLD publishes GICS names while the funds inside
// it publish Hamilton ones, so "Information Technology" has to reach holdings
// filed under "Technology" — see src/data/sector-canon.ts, which does that
// rolling up and reports whether it had to.

const Row: Component<{ leaf: Leaf; root: string; muted?: boolean }> = (props) => {
    // Where it is held, but only worth saying when that is not the fund on
    // screen — for a fund holding companies directly it would be the same word
    // on every row.
    const via = () => props.leaf.via.filter((v) => v !== props.root);

    return (
        <div class='flex h-row items-center justify-between gap-2 border-b px-2'>
            <div class='flex min-w-0 items-baseline gap-2'>
                <Show when={props.leaf.ticker}>
                    <Text weight='medium' class='shrink-0'>
                        {props.leaf.ticker}
                    </Text>
                </Show>
                <Text tone='muted' class='truncate'>
                    {props.leaf.name}
                </Text>
            </div>
            <div class='flex shrink-0 items-baseline gap-2'>
                <Show when={via().length > 0}>
                    {/* Clicking the fund it came through opens that fund, which
                        is where the weight can be checked against the issuer. */}
                    <button type='button' class='cursor-default rounded px-1 hover:bg-muted' title={`Open ${via()[0]}`} onClick={() => drillInto(via()[0])}>
                        <Text tone='muted'>{via().join(' · ')}</Text>
                    </button>
                </Show>
                <Text tone={props.muted ? 'muted' : undefined}>{formatPercent(props.leaf.weight, 1)}</Text>
            </div>
        </div>
    );
};

const SectorPanel: Component = () => {
    const symbol = () => selectedSymbol();
    const sector = () => openSector() ?? '';

    const leaves = createMemo(() => lookThrough(symbol()));
    const match = createMemo(() => matchSector(sector(), leaves(), (leaf) => leaf.sector));

    const weight = createMemo(() => match().items.reduce((sum, l) => sum + l.weight, 0));

    /** What the issuer said this slice was worth, to show the mapping against.
        A look-through that lands well off it has either missed holdings or
        gathered too many, and that is worth seeing rather than trusting. */
    const published = createMemo(() => {
        const breakdown = allocationsByKind(symbol()).find((b) => /sector/i.test(b.kind));
        return breakdown?.slices.find((s) => s.name === sector())?.weight ?? null;
    });

    // Only positions, and only ones we could have classified. A cash line
    // belongs to no sector and is not a gap in the mapping.
    const unclassified = createMemo(() => leaves().filter((l) => l.ticker != null && l.sector == null));

    return (
        <Panel>
            <Toolbar>
                <ToolbarSection class='min-w-0'>
                    <Heading level={2} class='truncate pl-2'>
                        {sector()}
                    </Heading>
                </ToolbarSection>
                <ToolbarSection>
                    <Text tone='muted' class='whitespace-nowrap pr-2'>
                        {formatPercent(weight(), 1)} of {symbol()}
                        <Show when={published() != null}>{` · ${formatPercent(published(), 1)} published`}</Show>
                    </Text>
                    <Button size='fill' icon={<CrossIcon />} title='Close' onClick={closeSector} />
                </ToolbarSection>
            </Toolbar>

            <div class='flex-1 min-h-0 overflow-y-auto scrollbar-hide'>
                {/* Said once, at the top, rather than marked on every row: the
                    whole list was gathered the same way. */}
                <Show when={match().how === 'rolled'}>
                    <div class='flex h-row items-center border-b bg-muted px-2'>
                        <Text tone='muted' class='truncate'>
                            Matched by sector group — these funds file under different names
                        </Text>
                    </div>
                </Show>
                <Show when={isNested(symbol())}>
                    <div class='flex h-row items-center border-b bg-muted px-2'>
                        <Text tone='muted' class='truncate'>
                            Looked through {symbol()}'s funds to what they hold
                        </Text>
                    </div>
                </Show>

                <Show
                    when={match().items.length > 0}
                    fallback={
                        <Text tone='muted' class='block px-2 py-4 text-center'>
                            {match().how === 'not-a-sector'
                                ? `${sector()} is not a line of business — it is how ${symbol()} labels the part of itself that spans several`
                                : leaves().every((l) => l.sector == null)
                                  ? `${symbol()} publishes this breakdown but not which holdings make it up`
                                  : 'No holding is classified in this sector'}
                        </Text>
                    }>
                    <For each={match().items}>{(leaf) => <Row leaf={leaf} root={symbol()} />}</For>
                </Show>

                {/* Named rather than dropped: a holding the app has not placed is
                    missing from exactly one of these lists, and saying which one
                    beats letting a slice quietly under-report. */}
                <Show when={unclassified().length > 0}>
                    <div class='flex h-row items-center justify-between gap-2 border-t border-b bg-muted px-2'>
                        <Text tone='muted'>Not classified</Text>
                        <Text tone='muted'>{formatPercent(unclassified().reduce((sum, l) => sum + l.weight, 0), 1)}</Text>
                    </div>
                    <For each={unclassified()}>{(leaf) => <Row leaf={leaf} root={symbol()} muted />}</For>
                </Show>
            </div>
        </Panel>
    );
};

export default SectorPanel;
